import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ExternalLink, ArrowRight } from 'lucide-react';
import { databaseService } from '../../lib/appwrite';

const GALLERIES_COLLECTION = 'gallery_carousels';

// Single Carousel with TRUE Continuous Infinite Loop
function InfiniteCarousel({ gallery }) {
  const trackRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [calculatedAspectRatio, setCalculatedAspectRatio] = useState(null);
  const animationRef = useRef({ rafId: null, dragRafId: null });
  const positionRef = useRef(0);

  const originalImages = gallery.images || [];
  const imageCount = originalImages.length;
  
  if (imageCount === 0) return null;

  // Multiply images 4 times (sufficient for seamless loop without excessive DOM nodes)
  const multiplier = 4;
  const repeatedImages = [];
  for (let i = 0; i < multiplier; i++) {
    repeatedImages.push(...originalImages);
  }
  
  const scrollInterval = gallery.scrollInterval || 3000;
  const imageWidthPercent = 20; // 5 images visible
  const totalImages = imageCount * multiplier;
  const singleImageWidth = 100 / 5; // Each image is 20% of viewport

  // Calculate Aspect Ratio if Auto-Detect is enabled
  // Calculate Aspect Ratio - Auto-detect is now the default for better UX
  useEffect(() => {
    // If set to specifically square/portrait, respect that. 
    // If 'auto' OR 'aspect-[4/3]' (the default), try to detect from image.
    const shouldAutoDetect = gallery.aspectRatio === 'auto' || !gallery.aspectRatio || gallery.aspectRatio === 'aspect-[4/3]';
    
    if (shouldAutoDetect && originalImages.length > 0) {
      const img = new Image();
      img.src = originalImages[0].url;
      img.onload = () => {
        const ratio = img.height / img.width;
        // Construct a custom tailwind-like arbitrary value or inline style
        // We will store it to apply as inline style for maximum precision
        setCalculatedAspectRatio({ paddingBottom: `${ratio * 100}%` });
      };
    } else {
      setCalculatedAspectRatio(null);
    }
  }, [gallery.aspectRatio, originalImages]);

  // Manual Drag Logic
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startPosRef = useRef(0);
  const containerRef = useRef(null);

  const handlePointerDown = (e) => {
    // Check for primary pointer and left click (or touch)
    if (!e.isPrimary || e.button !== 0) return;
    
    // Prevent default browser dragging behavior
    e.preventDefault();
    
    setIsDraggingRef.current = true;
    startXRef.current = e.clientX;
    startPosRef.current = positionRef.current;
    
    // Pause auto-scroll immediately
    setIsHovered(true);
    
    // Capture pointer on CONTAINER instead of track
    if (containerRef.current) {
      containerRef.current.setPointerCapture(e.pointerId);
      containerRef.current.style.cursor = 'grabbing';
    }
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current || !trackRef.current) return;

    // Throttle using requestAnimationFrame for smooth performance
    if (!animationRef.current.dragRafId) {
      animationRef.current.dragRafId = requestAnimationFrame(() => {
        const deltaX = e.clientX - startXRef.current;
        
        // Calculate percentage relative to the TRACK's total width
        const trackWidth = trackRef.current.scrollWidth;
        // Protect against zero width
        if (trackWidth === 0) {
          animationRef.current.dragRafId = null;
          return;
        }

        const pixelToPercent = (deltaX / trackWidth) * 100;
        
        // Update position
        positionRef.current = startPosRef.current - pixelToPercent;
        
        trackRef.current.style.transform = `translateX(-${positionRef.current}%)`;
        
        animationRef.current.dragRafId = null;
      });
    }
  };

  const handlePointerUp = (e) => {
    if (!isDraggingRef.current) return;
    
    // Cancel any pending drag animation frame
    if (animationRef.current.dragRafId) {
      cancelAnimationFrame(animationRef.current.dragRafId);
      animationRef.current.dragRafId = null;
    }
    
    isDraggingRef.current = false;
    
    // Resume logic
    if (e.pointerType === 'touch') {
      setTimeout(() => setIsHovered(false), 1000);
    } else {
       // Mouse resume relies on onMouseLeave
       // But if we are inside container, we should arguably check hovering status?
       // Current logic: setIsHovered(false) in onMouseLeave.
    }

    if (containerRef.current) {
      containerRef.current.releasePointerCapture(e.pointerId);
      containerRef.current.style.cursor = 'grab';
    }
  };

  // Continuous smooth animation using requestAnimationFrame
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let lastTime = Date.now();
    const pixelsPerSecond = (singleImageWidth / (scrollInterval * 2)) * 1000; // Speed calculation

    const animate = () => {
      // Only auto-scroll if NOT hovered and NOT dragging
      if (!isHovered && !isDraggingRef.current) {
        const now = Date.now();
        const delta = now - lastTime;
        lastTime = now;

        // Move forward continuously
        positionRef.current += (pixelsPerSecond * delta) / 1000;

        // When we've scrolled through half the images, seamlessly loop back
        const halfwayPoint = (totalImages / 2) * singleImageWidth;
        // Normalize position to keep numbers distinct but consistent
        // Use a loop boundary
        if (positionRef.current >= halfwayPoint) {
            positionRef.current -= halfwayPoint; // Reset but keep flow
        } else if (positionRef.current < 0) {
            // Handle reverse drag
            positionRef.current += halfwayPoint;
        }

        track.style.transform = `translateX(-${positionRef.current}%)`;
      } else {
        lastTime = Date.now();
      }

      animationRef.current.rafId = requestAnimationFrame(animate);
    };

    animationRef.current.rafId = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current.rafId) {
        cancelAnimationFrame(animationRef.current.rafId);
      }
      if (animationRef.current.dragRafId) {
        cancelAnimationFrame(animationRef.current.dragRafId);
      }
    };
  }, [isHovered, singleImageWidth, scrollInterval, totalImages]);

  const getLabelStyle = (design) => {
    // Position: bottom-[5%] leaves 5% free space below the label
    const baseStyle = "absolute bottom-[5%] left-1/2 -translate-x-1/2 px-4 py-2 text-center max-w-[85%] font-bold z-10";
    switch (design) {
      case 'vintage-paper':
        return `${baseStyle} bg-[#f5f0e1] text-[#2d8a8a] italic shadow-lg rotate-[-1deg] rounded-sm text-xs`;
      case 'tape-label':
        return `${baseStyle} bg-[#f8f4e8] text-[#2d8a8a] italic shadow-lg skew-x-[-2deg] text-xs`;
      case 'ribbon':
        return `${baseStyle} bg-white/95 text-gray-800 italic shadow-lg rounded text-xs`;
      case 'neon-glow':
        return `${baseStyle} bg-black/90 text-[#00ff88] rounded text-xs shadow-[0_0_10px_#00ff88,0_0_20px_#00ff88] border border-[#00ff88]`;
      case 'retro-badge':
        return `${baseStyle} bg-[#d4a574] text-[#3d2914] rounded-lg border-2 border-[#3d2914] text-xs uppercase tracking-wider`;
      case 'glass-card':
        return `${baseStyle} bg-white/20 backdrop-blur-md text-white rounded-lg border border-white/30 text-xs`;
      case 'chalk-board':
        return `${baseStyle} bg-[#2a3d2a] text-[#e8e8e8] rounded border border-[#e8e8e8]/50 text-xs tracking-wide`;
      case 'polaroid':
        return `${baseStyle} bg-white text-gray-700 shadow-xl rounded-sm pb-4 text-xs`;
      case 'stamp':
        return `${baseStyle} bg-white/90 text-red-600 border-2 border-red-600 border-dashed rounded text-xs uppercase rotate-[-3deg]`;
      case 'typewriter':
        return `${baseStyle} bg-[#f9f6f0] text-gray-800 font-mono rounded-sm shadow text-xs tracking-widest`;
      case 'cinema':
        return `${baseStyle} bg-black text-[#ffd700] border-y-2 border-[#ffd700] text-xs uppercase tracking-[0.2em]`;
      case 'wooden':
        return `${baseStyle} bg-gradient-to-b from-[#8b5a2b] to-[#6b4423] text-[#f5deb3] rounded shadow-lg text-xs border border-[#5a3d1a]`;
      case 'neon-pink':
        return `${baseStyle} bg-black/90 text-[#ff1493] rounded text-xs shadow-[0_0_10px_#ff1493,0_0_20px_#ff1493] border border-[#ff1493]`;
      case 'newspaper':
        return `${baseStyle} bg-[#f5f5dc] text-gray-900 font-serif rounded-sm text-xs border border-gray-400`;
      case 'gradient-modern':
        return `${baseStyle} bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-xs shadow-lg`;
      default:
        return `${baseStyle} bg-[#f5f0e1] text-[#2d8a8a] italic shadow-lg rounded-sm text-xs`;
    }
  };

  return (
    <div className="mb-8">
      {/* Gallery Header */}
      <div className="flex items-center justify-between mb-3 px-2">
        <h3 className="text-base md:text-lg font-bold" style={{ color: '#105652' }}>
          {gallery.title}
        </h3>
        {gallery.eventLink && (
          <a
            href={gallery.eventLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1 text-white text-xs font-medium rounded-lg transition-colors hover:opacity-90"
            style={{ backgroundColor: '#105652' }}
          >
            Event Link
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      <div className="relative group">
        {/* Vanishing effect mask - Left */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-20 z-20 pointer-events-none"
          style={{ 
            background: 'linear-gradient(to right, #FFFAEB 0%, transparent 100%)'
          }}
        />
        
        {/* Vanishing effect mask - Right */}
        <div 
          className="absolute right-0 top-0 bottom-0 w-20 z-20 pointer-events-none"
          style={{ 
            background: 'linear-gradient(to left, #FFFAEB 0%, transparent 100%)'
          }}
        />

        {/* Images Track - Continuous scroll */}
        <div 
          ref={containerRef}
          className="overflow-hidden cursor-grab active:cursor-grabbing"
          style={{ touchAction: 'pan-y' }} // Allow vertical scroll, handle horizontal manually
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => {
             // Only set false if not currently dragging
             if (!isDraggingRef.current) setIsHovered(false);
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div
            ref={trackRef}
            className="flex"
            style={{ 
              willChange: 'transform',
              transform: 'translate3d(0, 0, 0)', // Force GPU acceleration
              backfaceVisibility: 'hidden', // Optimize rendering
              perspective: 1000 // Enable 3D rendering context
            }}
          >
            {repeatedImages.map((img, idx) => (
              <div
                key={`${img.id || idx}-${idx}`}
                className="flex-shrink-0 px-1 w-[60%] md:w-[20%] select-none" // prevent text selection while dragging
              >
                <div 
                  className={`relative rounded-lg overflow-hidden border-2 bg-gray-100 min-h-[100px] md:min-h-[150px]
                    ${!calculatedAspectRatio ? (
                      gallery.aspectRatio === 'aspect-square' || gallery.aspectRatio === 'aspect-[1/1]' ? 'aspect-[1/1]' :
                      gallery.aspectRatio === 'aspect-[3/4]' ? 'aspect-[3/4]' :
                      gallery.aspectRatio === 'aspect-[9/16]' ? 'aspect-[9/16]' :
                      'aspect-[4/3]'
                    ) : ''}
                  `}
                  style={{ 
                    borderColor: '#105652',
                    ...(calculatedAspectRatio ? { height: 0, ...calculatedAspectRatio } : {}) // Apply calculated ratio as padding-bottom hack if auto
                  }}
                >
                  {/* If custom ratio, we need absolute positioning for the image to fill the padded container */}
                  <img
                    src={img.url}
                    alt={img.title || `Image ${idx + 1}`}
                    className={`w-full h-full object-cover transition-transform duration-500 ${calculatedAspectRatio ? 'absolute inset-0' : ''}`}
                    style={{
                      transform: `scale(${img.zoom || 1})`,
                      transformOrigin: `${img.x || 50}% ${img.y || 50}%`
                    }}
                    draggable={false}
                  />
                  {img.title && img.title.trim().length > 0 && (
                    <div className={getLabelStyle(gallery.labelDesign || 'ribbon')}>
                      <span style={{ fontSize: gallery.titleFontSize || '10px' }}>
                        {img.title}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hover indicator */}
        <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute left-2 top-1/2 -translate-y-1/2 text-white text-xs bg-black/50 px-2 py-1 rounded">
            Hover to pause
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Project Gallery Component
export default function ProjectGallery(props) {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGalleries();
  }, []);

  const loadGalleries = async () => {
    try {
      const result = await databaseService.listDocuments(
        GALLERIES_COLLECTION
      );
      if (result.success && result.data?.documents) {
        const featured = result.data.documents
          .filter(g => g.featured)
          .map(g => {
            const images = typeof g.images === 'string' ? JSON.parse(g.images || '[]') : (g.images || []);
            return {
              ...g,
              images: images.map(img => ({
                ...img,
                url: img.url || ''
              }))
            };
          })
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setGalleries(featured);
      }
    } catch (error) {
      console.error('Error loading galleries:', error);
    } finally {
      setLoading(false);
      if (props.onLoad) {
        props.onLoad();
      }
    }
  };

  if (loading) return null;
  if (galleries.length === 0) return null;

  return (
    <section 
      id="project-gallery" 
      className="py-10 relative overflow-hidden"
      style={{ 
        background: '#FFFAEB',
        backgroundImage: `linear-gradient(rgba(16, 86, 82, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 86, 82, 0.03) 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }}
    >
      <div className="container mx-auto px-4 lg:pl-28">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#105652' }}>
            Galleries
          </h2>
        </div>

        {galleries.map((gallery) => (
          <InfiniteCarousel key={gallery.$id} gallery={gallery} />
        ))}

        <div className="text-center mt-4">
          <Link
            to="/project-gallery"
            className="group inline-flex items-center gap-2 px-6 py-2 border-2 border-[#105652] text-[#105652] font-medium rounded-none hover:bg-[#105652] hover:text-white transition-all duration-300"
          >
            <span className="tracking-wider text-sm uppercase">View All Galleries</span>
            <svg 
              className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
